import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkAssignCategoriesToProgramOptions,
  BulkAssignCategoriesToProgramResponse,
  BulkCreateCategoryOptions,
  BulkCreateCategoryResponse,
  CategoriesQueryBuilder,
  Category,
  CategoryQuery,
  ListCategoriesOptions,
  ListCategoriesResponse,
  QueryCategoriesResponse,
  UpdateCategory,
  bulkAssignCategoriesToProgram as universalBulkAssignCategoriesToProgram,
  bulkCreateCategory as universalBulkCreateCategory,
  createCategory as universalCreateCategory,
  deleteCategory as universalDeleteCategory,
  getCategory as universalGetCategory,
  listCategories as universalListCategories,
  queryCategories as universalQueryCategories,
  typedQueryCategories as universalTypedQueryCategories,
  updateCategory as universalUpdateCategory,
} from './achievements-categories-v3-category-categories.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/online-programs' };

export function createCategory(
  httpClient: HttpClient
): CreateCategorySignature {
  return (category: Category) =>
    universalCreateCategory(
      category,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateCategorySignature {
  /**
   * Creates a new Category
   * @param - Category to be created
   * @returns The created Category
   */
  (category: Category): Promise<
    NonNullablePaths<Category, `label` | `assignedTo`, 2>
  >;
}

export function bulkCreateCategory(
  httpClient: HttpClient
): BulkCreateCategorySignature {
  return (categories: Category[], options?: BulkCreateCategoryOptions) =>
    universalBulkCreateCategory(
      categories,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateCategorySignature {
  /**
   * Creates new categories in a bulk
   * @param - List of categories you want to create
   */
  (categories: Category[], options?: BulkCreateCategoryOptions): Promise<
    NonNullablePaths<
      BulkCreateCategoryResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.label`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function getCategory(httpClient: HttpClient): GetCategorySignature {
  return (categoryId: string) =>
    universalGetCategory(
      categoryId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetCategorySignature {
  /**
   * Get a Category by id
   * @param - Id of the Category to retrieve
   * @returns The retrieved Category
   */
  (categoryId: string): Promise<
    NonNullablePaths<Category, `label` | `assignedTo`, 2>
  >;
}

export function listCategories(
  httpClient: HttpClient
): ListCategoriesSignature {
  return (options?: ListCategoriesOptions) =>
    universalListCategories(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListCategoriesSignature {
  /**
   * Get list of categories by list of ids
   */
  (options?: ListCategoriesOptions): Promise<
    NonNullablePaths<
      ListCategoriesResponse,
      `categories` | `categories.${number}.label`,
      4
    >
  >;
}

export function updateCategory(
  httpClient: HttpClient
): UpdateCategorySignature {
  return (
    _id: string,
    category: NonNullablePaths<UpdateCategory, `revision`, 2>
  ) =>
    universalUpdateCategory(
      _id,
      category,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateCategorySignature {
  /**
   * Update a Category, supports partial update
   * Pass the latest `revision` for a successful update
   * @param - Category ID.
   * @returns The updated Category
   */
  (
    _id: string,
    category: NonNullablePaths<UpdateCategory, `revision`, 2>
  ): Promise<NonNullablePaths<Category, `label` | `assignedTo`, 2>>;
}

export function deleteCategory(
  httpClient: HttpClient
): DeleteCategorySignature {
  return (categoryId: string) =>
    universalDeleteCategory(
      categoryId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteCategorySignature {
  /**
   * Delete a Category
   * @param - Id of the Category to delete
   */
  (categoryId: string): Promise<void>;
}

export function queryCategories(
  httpClient: HttpClient
): QueryCategoriesSignature {
  return () =>
    universalQueryCategories(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryCategoriesSignature {
  /**
   * Query Categories using [WQL - Wix Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language)
   */
  (): CategoriesQueryBuilder;
}

export function typedQueryCategories(
  httpClient: HttpClient
): TypedQueryCategoriesSignature {
  return (query: CategoryQuery) =>
    universalTypedQueryCategories(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryCategoriesSignature {
  /** */
  (query: CategoryQuery): Promise<
    NonNullablePaths<
      QueryCategoriesResponse,
      `categories` | `categories.${number}.label`,
      4
    >
  >;
}

export function bulkAssignCategoriesToProgram(
  httpClient: HttpClient
): BulkAssignCategoriesToProgramSignature {
  return (programId: string, options?: BulkAssignCategoriesToProgramOptions) =>
    universalBulkAssignCategoriesToProgram(
      programId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkAssignCategoriesToProgramSignature {
  /**
   * Assign or unassign categories to a program
   * @param - program ID which categories are changing
   */
  (programId: string, options?: BulkAssignCategoriesToProgramOptions): Promise<
    NonNullablePaths<
      BulkAssignCategoriesToProgramResponse,
      | `results`
      | `results.${number}.action`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.label`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export {
  AccountInfo,
  Action,
  ActionEvent,
  ApplicationError,
  BulkActionMetadata,
  BulkAssignCategoriesToProgramOptions,
  BulkAssignCategoriesToProgramRequest,
  BulkAssignCategoriesToProgramResponse,
  BulkAssignCategoryResult,
  BulkCategoryResult,
  BulkCreateCategoryOptions,
  BulkCreateCategoryRequest,
  BulkCreateCategoryResponse,
  CategoriesAssignedToProgram,
  CategoriesQueryBuilder,
  CategoriesQueryResult,
  Category,
  CategoryAssignedProgramsUpdated,
  CategoryQuerySpec,
  CreateCategoryRequest,
  CreateCategoryResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteCategoryRequest,
  DeleteCategoryResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ExtendedFields,
  GetCategoryRequest,
  GetCategoryResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  ListCategoriesOptions,
  ListCategoriesRequest,
  ListCategoriesResponse,
  MessageEnvelope,
  QueryCategoriesRequest,
  QueryCategoriesResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  UpdateCategory,
  UpdateCategoryRequest,
  UpdateCategoryResponse,
  WebhookIdentityType,
  utils,
} from './achievements-categories-v3-category-categories.universal.js';
